import { useEffect, useRef, useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  const [isVisible, setIsVisible] = useState<{ [key: string]: boolean }>({});
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    Object.keys(sectionRefs.current).forEach((key) => {
      const element = sectionRefs.current[key];
      if (element) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible((prev) => ({ ...prev, [key]: true }));
            }
          },
          { threshold: 0.1 }
        );
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div className="bg-sacred-moss">
      <Navigation />

      {/* HERO SECTION */}
      <section className="relative min-h-screen grid grid-cols-1 lg:grid-cols-2">
        {/* Left - Image */}
        <div className="relative h-96 lg:h-auto overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_0f4f1213518f51102f80710c22b6a4a4_80850366.jpg"
            alt="Lola Selkara"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-deep-burgundy opacity-40"></div>
        </div>

        {/* Right - Content */}
        <div className="bg-sacred-moss flex items-center justify-center px-8 lg:px-16 py-24 relative">
          {/* Botanical corner accent */}
          <div className="absolute bottom-16 right-16 w-24 h-24 opacity-[0.08]">
            <svg viewBox="0 0 100 100" className="w-full h-full text-ancient-gold">
              <path
                d="M50 90 Q60 70 50 50 Q30 40 10 50 M50 90 Q70 80 90 90"
                stroke="currentColor"
                fill="none"
                strokeWidth="0.5"
              />
            </svg>
          </div>

          <div className="max-w-lg">
            <h1 className="font-cormorant text-ancient-gold tracking-widest-plus text-4xl mb-6">
              LOLA SELKARA
            </h1>
            <div className="w-24 h-px bg-ancient-gold mb-6"></div>
            <p className="font-cormorant text-moon-ivory italic text-xl mb-8">
              Founder of House of Selkara
            </p>
            
            {/* Crescent Moon */}
            <div className="mt-12">
              <svg className="w-16 h-16 text-ancient-gold" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* THE STORY SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['story'] = el; }}
        className={`bg-moon-ivory py-24 px-8 transition-all duration-1200 ${
          isVisible['story'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="font-cormorant text-deep-burgundy tracking-widest-plus text-3xl mb-12 text-left pl-8">
            THE STORY
          </h2>

          <div className="space-y-8 text-left">
            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose">
              <span className="font-cormorant text-ancient-gold text-6xl float-left mr-4 leading-none">I</span>
              spent years showing up as a version of myself built for everyone else's comfort. Successful on paper. Exhausted underneath. There was a distance — quiet and growing — between who I was performing and who I actually was.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose">
              I had been trained to be palatable. To soften my edges. To make my power easier for others to digest. And I was good at it. So good that I almost forgot there was another version of me underneath.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose">
              <span className="font-cormorant text-ancient-gold text-6xl float-left mr-4 leading-none">T</span>
              he return didn't happen all at once. It was a series of small, deliberate choices. To stop performing. To stop shrinking. To stop waiting for permission to take up space. And as I came back to myself, I realized: this is the work.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose">
              Not the work of becoming someone new. The work of remembering who you were before the world told you who to be. The work of excavating the woman who has been buried under years of conditioning, expectation, and performance.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose">
              <span className="font-cormorant text-ancient-gold text-6xl float-left mr-4 leading-none">H</span>
              ouse of Selkara was born from that return. It is the container I wish I had when I was in the thick of it. A place where women are seen — not as projects to be fixed, but as sovereign beings who have simply forgotten their power.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose">
              This is not about empowerment. Empowerment implies you need someone to give you power. You don't. You need someone to witness you remembering that you already have it.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose">
              That is what happens here. Women come to be seen at the level of their soul. To be called forward into the most sovereign version of themselves. To stop performing and start arriving.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* THE WORK SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['work'] = el; }}
        className={`bg-sacred-moss py-24 px-8 relative overflow-hidden transition-all duration-1200 ${
          isVisible['work'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Botanical corner accent */}
        <div className="absolute top-8 right-8 w-32 h-32 opacity-[0.08]">
          <svg viewBox="0 0 100 100" className="w-full h-full text-ancient-gold">
            <path
              d="M50 10 Q60 30 50 50 Q30 60 10 50 M50 10 Q70 20 90 10"
              stroke="currentColor"
              fill="none"
              strokeWidth="0.5"
            />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto">
          <h2 className="font-cormorant text-ancient-gold tracking-widest-plus text-3xl mb-12 text-center">
            THE WORK
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div className="space-y-8">
              <div>
                <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                  What I Do
                </h3>
                <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                  I work with women who are ready to stop performing and start arriving. Women who know there is a version of themselves underneath everything life has layered on top — and they are ready to go back for her.
                </p>
              </div>

              <div>
                <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                  How I Do It
                </h3>
                <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                  Through deep diagnostic work, embodied practice, and relentless truth-telling. I see what others cannot. I hear what is not being said. And I call women forward into the most sovereign version of themselves.
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <div>
                <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                  Who This Is For
                </h3>
                <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                  The woman who is done shrinking. Done performing. Done waiting for permission. She knows she is meant for more — and she is ready to do the work to claim it.
                </p>
              </div>

              <div>
                <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                  What Changes
                </h3>
                <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                  Everything. She stops performing and starts arriving. She stops shrinking and starts taking up space. She stops waiting for permission and starts claiming what is hers. She becomes undeniable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* TESTIMONIAL SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['testimonial'] = el; }}
        className={`bg-deep-burgundy py-24 px-8 transition-all duration-1200 ${
          isVisible['testimonial'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-3xl mx-auto text-center">
          <blockquote className="font-cormorant italic text-moon-ivory text-2xl md:text-3xl leading-relaxed mb-8">
            "Working with Lola was the first time I felt truly seen. Not as a problem to be solved, but as a woman who had simply forgotten her power. She helped me remember. And everything changed."
          </blockquote>
          
          <div className="w-24 h-px bg-ancient-gold mx-auto mb-6"></div>
          
          <p className="font-cormorant text-ancient-gold tracking-wider">
            — Sarah M.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* CTA SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['cta'] = el; }}
        className={`bg-moon-ivory py-24 px-8 transition-all duration-1200 ${
          isVisible['cta'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-cormorant italic text-deep-burgundy text-3xl md:text-4xl leading-relaxed mb-12">
            Ready to come home to yourself?
          </h2>

          <button
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="inline-block border-2 border-deep-burgundy text-deep-burgundy font-cormorant tracking-widest-plus px-16 py-5 hover:bg-deep-burgundy hover:text-moon-ivory transition-all duration-500 cursor-pointer whitespace-nowrap"
          >
            EXPLORE OFFERINGS
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}